import React, { useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import Breadcrumb from "../components/ui/Breadcrumb";
import PageHeader from "../components/ui/PageHeader";
import Pagination from "../components/ui/Pagination";
import RefreshButton from "../components/ui/RefreshButton";
import TableSkeleton from "../components/ui/TableSkeleton";
import EmptyState from "../components/ui/EmptyState";
import ErrorState from "../components/ui/ErrorState";
import SprintsTable from "../components/sprint/SprintsTable";
import SprintSelectionBar from "../components/sprint/SprintSelectionBar";
import { useSprints } from "../hooks/useSprints";
import { useSprintSelection } from "../hooks/useSprintSelection";
import { DEFAULT_PAGE_SIZE, totalPagesOf } from "../lib/pagination";
import { resolveProjectName } from "../lib/projectNameCache";
import { paths } from "../routes/paths";
import "./Sprints.css";

const SKELETON_COLUMNS = [
  { key: "check", className: "col-check", bars: 0 },
  { key: "name", label: "Sprint", bars: 2 },
  { key: "status", label: "Situação", className: "col-status" },
  { key: "due", label: "Encerramento", className: "col-due" },
];

function analyzeLabel(count) {
  if (count === 0) return "Analisar sprints";
  return count === 1 ? "Analisar 1 sprint" : `Analisar ${count} sprints`;
}

function Sprints() {
  const { projectId } = useParams();
  const location = useLocation();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const { sprints, loading, error, reload } = useSprints(projectId);

  // A seleção é do projeto inteiro, não da página: o hook continua vendo a
  // lista completa, e é ela que alimenta a contagem e o botão de análise.
  const selection = useSprintSelection(sprints);

  const projectName = resolveProjectName(projectId, location.state, sprints);
  const selectedSprints = sprints.filter((sprint) => selection.isSelected(sprint.id));
  const hasSprints = sprints.length > 0;
  const canAnalyze = selectedSprints.length > 0;
  const showSelectionBar = loading || (!error && hasSprints);

  // /sprints não aceita offset/limit, então o recorte é do cliente — depois
  // da ordenação, que o hook já aplicou sobre o projeto todo.
  const totalPages = totalPagesOf(sprints.length, pageSize);
  const currentPage = Math.min(page, Math.max(totalPages, 1));
  const pageSprints = sprints.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const analyzeState = {
    projectName,
    sprintNames: Object.fromEntries(selectedSprints.map((sprint) => [sprint.id, sprint.name])),
  };

  return (
    <>
      <Breadcrumb
        items={[
          { label: projectName, to: paths.projects },
          { label: "Sprints" },
        ]}
      />

      <PageHeader title="Sprints">
        <RefreshButton onClick={reload} loading={loading} />
        {/* aria-disabled não impede a ativação: sem o preventDefault, um
            clique navegaria para uma análise sem sprint nenhuma. */}
        <Link
          className={canAnalyze ? "btn btn-primary" : "btn btn-disabled"}
          to={paths.inconsistencies(projectId, selection.selectedIds)}
          state={analyzeState}
          aria-disabled={!canAnalyze}
          onClick={(event) => {
            if (!canAnalyze) event.preventDefault();
          }}
        >
          {analyzeLabel(selectedSprints.length)}
        </Link>
      </PageHeader>

      <div className="card data-card">
        {/* A barra fica de pé durante o carregamento para o topo do cartão não
            saltar quando o esqueleto dá lugar à tabela. */}
        {showSelectionBar && <SprintSelectionBar count={selectedSprints.length} />}

        {loading && <TableSkeleton columns={SKELETON_COLUMNS} label="Carregando sprints" />}

        {!loading && error && (
          <ErrorState
            title="Não foi possível carregar as sprints"
            message="Verifique sua conexão e tente novamente."
            onRetry={reload}
          />
        )}

        {!loading && !error && !hasSprints && (
          <EmptyState
            icon="folder"
            title="Nenhuma sprint encontrada neste projeto"
            help="O projeto pode não ter versões cadastradas no Redmine."
            action={
              <Link className="btn btn-secondary" to={paths.projects}>
                Voltar para Projetos
              </Link>
            }
          />
        )}

        {!loading && !error && hasSprints && (
          <SprintsTable
            sprints={pageSprints}
            projectId={projectId}
            projectName={projectName}
            selection={selection}
          />
        )}
      </div>

      {!loading && !error && hasSprints && (
        <>
          <Pagination
            label="Paginação das sprints"
            page={currentPage}
            totalPages={totalPages}
            total={sprints.length}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </>
      )}
    </>
  );
}

export default Sprints;
